import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Fragment, useId, type ReactNode } from "react";

import { StyledAnchor } from "@/components/common/styles";
import FaqToggleIcon from "./FaqToggleIcon";
import { type FaqAnswer, type FaqRun, groupFaqAnswer } from "./faqAnswer";
import { NOT_WIDE, marketingPalette } from "./tokens";
import { Lines } from "./Lines";
import { marketingHref, newTabProps } from "./marketingLinks";

/**
 * `.faq-main_toggle`: one question and its answer, as the published site draws
 * an FAQ everywhere. The answer stays with each surface as `children` so a
 * surface can still put something else under a question; every surface that
 * publishes an authored answer passes `FaqAnswerBody`.
 */
export default function FaqRow({
  question,
  expanded,
  defaultExpanded,
  onToggle,
  children,
}: {
  question: string;
  /** Omit for rows that open and close independently of their neighbours. */
  expanded?: boolean;
  /** Whether an independent row starts open. */
  defaultExpanded?: boolean;
  onToggle?: (open: boolean) => void;
  children: ReactNode;
}) {
  const controlled = expanded !== undefined;
  // MUI reads these back onto the answer as its `id` and `aria-labelledby`.
  const rowId = useId();

  return (
    <Accordion
      {...(controlled
        ? {
            expanded,
            onChange: (_: unknown, open: boolean) => onToggle?.(open),
          }
        : { defaultExpanded })}
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: "common.white",
        border: `1px solid ${marketingPalette.outline}`,
        borderRadius: "16px",
        // The published card is lifted, not just ruled.
        boxShadow: "rgba(0, 0, 0, 0.15) 0px 1px 12px -5px",
        // The rule darkens to the ink under the pointer, on the published easing.
        transition: "border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
        "&:hover": { borderColor: marketingPalette.ink },
        // Below the wide step the published card keeps its rule under the pointer.
        [NOT_WIDE]: { "&:hover": { borderColor: marketingPalette.outline } },
        "&::before": { display: "none" },
        "&:first-of-type, &:last-of-type": { borderRadius: "16px" },
        // `.faq-main_component`: the item's own 24 (16 below the wide step)
        // around the heading and its answer, so the heading stands inside it.
        p: "24px",
        [NOT_WIDE]: { p: "16px" },
      }}
    >
      <AccordionSummary
        id={`${rowId}-question`}
        aria-controls={`${rowId}-answer`}
        expandIcon={<FaqToggleIcon />}
        sx={{
          // The published button keeps 16px between the question and its toggle.
          gap: "16px",
          p: 0,
          minHeight: ROW_HEIGHT,
          "&.Mui-expanded": { minHeight: ROW_HEIGHT },
        }}
        slotProps={{ content: { sx: { m: 0, "&.Mui-expanded": { m: 0 } } } }}
      >
        {/* The heading is the `h3` MUI wraps this button in, which is where the
            published row carries it too: inside, it would not be valid. */}
        <Typography component="span" sx={FAQ_QUESTION}>
          {question}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pt: "24px", [NOT_WIDE]: { pt: "16px" } }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * The blocks of one answer, laid out as `.faq-main_content_text` lays them: a
 * plain block flow, so paragraphs sit straight under each other with nothing
 * between them, and neighbouring items gathered into the one list they publish.
 */
export function FaqAnswerBody({ answer }: { answer: FaqAnswer }) {
  return groupFaqAnswer(answer).map((group, index) =>
    group.kind === "paragraph" ? (
      <Typography
        key={`${index}-${group.runs[0]?.text.slice(0, 24) ?? ""}`}
        component="p"
        sx={FAQ_ANSWER}
      >
        <Runs runs={group.runs} />
      </Typography>
    ) : (
      <Box
        key={`${index}-list`}
        component={group.ordered ? "ol" : "ul"}
        sx={FAQ_ANSWER_LIST}
      >
        {group.items.map((runs, item) => (
          <Box
            component="li"
            key={`${item}-${runs[0]?.text.slice(0, 24) ?? ""}`}
            sx={FAQ_ANSWER_ITEM}
          >
            <Runs runs={runs} />
          </Box>
        ))}
      </Box>
    )
  );
}

/** The runs of one block, in the order they read. */
function Runs({ runs }: { runs: Array<FaqRun> }) {
  return runs.map((run, index) => {
    const key = `${index}-${run.text.slice(0, 24)}`;
    if (run.bold === undefined && run.href === undefined) {
      return <Lines key={key} text={run.text} />;
    }
    const text = run.bold ? (
      <Box component="strong" sx={{ fontWeight: 700 }}>
        <Lines text={run.text} />
      </Box>
    ) : (
      <Lines text={run.text} />
    );
    // An answer's anchor takes the published site's global `a` rule and nothing
    // else: the paragraph's own ink, no underline. `StyledAnchor` is that rule,
    // so a colour here would be inventing one the site does not draw.
    return run.href === undefined ? (
      <Fragment key={key}>{text}</Fragment>
    ) : (
      <StyledAnchor
        key={key}
        href={marketingHref(run.href)}
        {...newTabProps(run)}
      >
        {text}
      </StyledAnchor>
    );
  });
}

/** 24px of padding over a 35px heading, inside the row's own 1px rule. */
/** `.faq-main_toggle_button`: the question line inside the item's padding. */
const ROW_HEIGHT = "35.2px";

/** The answer under a question. */
const FAQ_ANSWER = {
  color: marketingPalette.ink,
  fontSize: 15,
  lineHeight: "24px",
};

/**
 * `ul, ol`, indented past its paragraphs and clearing 10px after itself.
 *
 * `overflow` is the published declaration, and it is load-bearing: it makes the
 * list its own block formatting context, so the last item's bottom margin stays
 * inside the list instead of collapsing into the 10px below it.
 */
const FAQ_ANSWER_LIST = { mt: 0, mb: "10px", pl: "40px", overflow: "hidden" };

/**
 * The published item is a step larger than the paragraph above it, and `li`
 * carries a `0.3rem` bottom margin, so the steps are spaced further apart than
 * their own line boxes.
 */
const FAQ_ANSWER_ITEM = {
  color: marketingPalette.ink,
  fontSize: 16,
  lineHeight: "24px",
  mb: "4.8px",
};

/** The question's own type. Exported: the blog draws the same rows itself. */
export const FAQ_QUESTION = {
  display: "flex",
  alignItems: "center",
  minHeight: "35px",
  color: marketingPalette.ink,
  fontSize: 17.6,
  lineHeight: "26.4px",
  fontWeight: 400,
  // Not a heavier weight: the published question is stroked, which thickens the
  // glyph without moving anything.
  WebkitTextStroke: "0.7px currentColor",
  [NOT_WIDE]: {
    fontSize: 16,
    lineHeight: "24px",
  },
};
